import { SendApplicantV2RequestDto } from "@/lib/interfaces/embed";
import { useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useFbclid } from "../../../hooks/use-fbclid";
import useApiRequest from "../../../lib/hooks/useRequest";
import { Button } from "../../../ui/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import { getReadableTextColor } from "./v2-helpers";

const formSchema = z.object({
  firstName: z.string().min(1, "Vorname ist erforderlich"),
  lastName: z.string().min(1, "Nachname ist erforderlich"),
  mail: z.string().email("Ungültige E-Mail-Adresse").min(1, "E-Mail ist erforderlich"),
  phone: z.string().min(1, "Telefonnummer ist erforderlich"),
});

type FormValues = z.infer<typeof formSchema>;

export function JobPostsV2ApplicationForm({
  jobOfferId,
  color,
  readOnly,
}: {
  jobOfferId: string;
  color: string;
  readOnly?: boolean;
}) {
  const { apiRequest } = useApiRequest();
  const captured = useFbclid();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      mail: "",
      phone: "",
    },
  });

  async function onSubmit(values: FormValues) {
    if (readOnly) return;
    setIsSubmitting(true);

    try {
      const applicationData: SendApplicantV2RequestDto = {
        firstName: values.firstName,
        lastName: values.lastName,
        mail: values.mail,
        phone: values.phone,
        belongsToJobOfferId: jobOfferId,
        ...(captured && { fbclid: captured.fbclid, fbclidCapturedAt: captured.capturedAt }),
        // Origin and path only, deliberately without the query string: a career page can be
        // linked with prefill parameters, and those would otherwise reach Meta in plain text.
        // Meta uses event_source_url at page level, so the query string adds nothing.
        eventSourceUrl: `${window.location.origin}${window.location.pathname}`,
      };

      const res = await apiRequest(
        `embed/send-applicant`,
        "POST",
        {
          body: applicationData,
          withoutAuth: true,
          customHeaders: {
            "x-api-key": process.env.REACT_APP_FORM_API_KEY || "",
          },
        },
        "v2.0",
      );

      if (res.status === 200 || res.status === 201) {
        toast({
          title: "Bewerbung erfolgreich gesendet!",
          description: "Wir werden uns bald bei Ihnen melden.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        form.reset();
      } else {
        throw new Error((res.error as string) || "Fehler beim Senden der Bewerbung");
      }
    } catch {
      toast({
        title: "Fehler beim Senden der Bewerbung",
        description: "Bitte versuchen Sie es später erneut.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div id="application-form" className="py-4">
      <div className="embed-job-form mx-auto w-full max-w-lg rounded-2xl bg-gray-900 text-white shadow-lg border border-white/10">
        <div className="p-8">
          <h2 className="text-xl md:text-2xl font-bold">Bewirb dich jetzt – in nur 60 Sekunden</h2>
          <p className="mt-2 text-sm text-white/70">Kein Lebenslauf nötig. Wir melden uns persönlich bei dir.</p>

          <div className="mt-6">
            <Form {...form}>
              <form onSubmit={readOnly ? (e) => e.preventDefault() : form.handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-white/80">Vorname *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Dein Vorname"
                            className="bg-white text-gray-900 placeholder:text-gray-400 border-transparent focus-visible:ring-2 focus-visible:ring-white/50"
                            {...field}
                            disabled={readOnly}
                          />
                        </FormControl>
                        <FormMessage className="text-xs text-red-300" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-white/80">Nachname *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Dein Nachname"
                            className="bg-white text-gray-900 placeholder:text-gray-400 border-transparent focus-visible:ring-2 focus-visible:ring-white/50"
                            {...field}
                            disabled={readOnly}
                          />
                        </FormControl>
                        <FormMessage className="text-xs text-red-300" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="mail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-white/80">E-Mail-Adresse *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Deine E-Mail"
                          className="bg-white text-gray-900 placeholder:text-gray-400 border-transparent focus-visible:ring-2 focus-visible:ring-white/50"
                          {...field}
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormMessage className="text-xs text-red-300" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-white/80">Telefonnummer *</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Deine Telefonnummer"
                          className="bg-white text-gray-900 placeholder:text-gray-400 border-transparent focus-visible:ring-2 focus-visible:ring-white/50"
                          {...field}
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormMessage className="text-xs text-red-300" />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={isSubmitting || readOnly}
                  className="embed-job-submit w-full font-semibold shadow-none"
                  style={{ backgroundColor: color, color: getReadableTextColor(color) }}
                >
                  {readOnly ? "Vorschau – keine Bewerbung möglich" : "Bewerbung absenden"}
                </Button>

                <p className="text-[11px] leading-relaxed text-white/60 text-center">
                  {readOnly
                    ? "In der eingebetteten Vorschau ist das Formular deaktiviert."
                    : "Kein Lebenslauf nötig. Deine Bewerbung ist vertraulich. Wir melden uns innerhalb von 24 Stunden."}
                </p>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
